import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  getHealth,
  getDiagnostics,
  getStock,
  runScreen,
  getFundsMeta,
  runFundScreen,
  getSwingMeta,
  getSwingTrades,
  runSwingScan
} from '../lib/api.js';

// Which sections the server can serve. Filled in once at the root before
// anything renders, so no page has to cope with it arriving late.
export const FeatureContext = createContext({ stocks: true, swing: true, funds: true });
export const useFeatures = () => useContext(FeatureContext);

// Who is signed in. The admin flag only decides what the UI offers; the server
// checks it again on every request that changes anything.
export const SessionContext = createContext({ username: null, admin: false });
export const useSession = () => useContext(SessionContext);

export function useHealth() {  const [health, setHealth] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getHealth().then(
      (h) => !cancelled && setHealth(h),
      (e) => !cancelled && setError(e.message)
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return { health, error };
}

/** Owns a screening run: progress log, result, and failure diagnostics. */
export function useScreenRun() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [log, setLog] = useState([]);
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const inFlight = useRef(false);

  const start = useCallback(async (body) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRunning(true);
    setFailure(null);
    setLog([]);
    setProgress({ percent: 0, message: 'Starting…' });

    try {
      const res = await runScreen(body, (p) => {
        setProgress(p);
        setLog((prev) => (prev[prev.length - 1] === p.message ? prev : [...prev, p.message]));
      });
      setResult(res);
    } catch (err) {
      // A failed run is nearly always one upstream being unreachable - name it.
      const checks = await getDiagnostics().then((d) => d.checks, () => null);
      setFailure({ message: err.message, checks });
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setFailure(null);
    setProgress(null);
  }, []);

  return { running, progress, log, result, failure, start, reset };
}

export function useFundsMeta() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getFundsMeta().then(
      (m) => !cancelled && setMeta(m),
      (e) => !cancelled && setError(e.message)
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return { meta, error };
}

/** Owns a mutual fund screening run. */
export function useFundRun() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const start = useCallback(async (body) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRunning(true);
    setError(null);
    setProgress({ phase: 'Starting\u2026', done: 0, total: 0 });

    try {
      setResult(await runFundScreen(body, setProgress));
    } catch (err) {
      setError(err.message);
      setResult(null);
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(null);
  }, []);

  return { running, progress, result, error, start, reset };
}

export function useSwingMeta() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getSwingMeta().then(
      (m) => !cancelled && setMeta(m),
      (e) => !cancelled && setError(e.message)
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return { meta, error };
}

/** Owns a swing scan. */
export function useSwingRun() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inFlight = useRef(false);

  const start = useCallback(async (body) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setRunning(true);
    setError(null);
    setProgress({ phase: 'Starting\u2026', done: 0, total: 0 });
    try {
      setResult(await runSwingScan(body, setProgress));
    } catch (err) {
      setError(err.message);
      setResult(null);
    } finally {
      inFlight.current = false;
      setRunning(false);
    }
  }, []);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
    setProgress(null);
  }, []);

  return { running, progress, result, error, start, reset };
}

/** Historical trades for a strategy. Loaded lazily so it never blocks a scan. */
export function useSwingTrades(mode, universe, enabled) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      setLog(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setLog(null);
    getSwingTrades(mode, universe).then(
      (l) => !cancelled && setLog(l),
      (e) => !cancelled && setError(e.message)
    ).finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [mode, universe, enabled, attempt]);

  return { log, loading, error, retry };
}

export function useStockReport(symbol, years) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol) {
      setReport(null);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setReport(null);

    getStock(symbol, years, false, controller.signal).then(
      (r) => {
        setReport(r);
        setLoading(false);
      },
      (err) => {
        if (err.name === 'AbortError') return;
        setError(err.message);
        setLoading(false);
      }
    );

    return () => controller.abort();
  }, [symbol, years]);

  return { report, error, loading };
}
